import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

import { batchesService, salesService } from "../../../services/metalguard";
import { useToast } from "components/Toast";

const fmt = (val) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })?.format(val);

function generateBatchId() {
  const now = new Date();
  const pad = (n, len = 2) => String(n)?.padStart(len, '0');
  const date = `${now?.getFullYear()}${pad(now?.getMonth() + 1)}${pad(now?.getDate())}`;
  const time = `${pad(now?.getHours())}${pad(now?.getMinutes())}${pad(now?.getSeconds())}`;
  const rand = Math.random()?.toString(36)?.substring(2, 5)?.toUpperCase();
  return `LOTE-${date}-${time}-${rand}`;
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: "'IBM Plex Sans', sans-serif" }}>{msg}</p>;
}

export default function SaveBatchModal({ isOpen, onClose, onSave, result, formData, existingBatchId }) {
  const { addToast } = useToast();
  const [batchName, setBatchName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (isOpen) {
      setBatchName(generateBatchId());
      setSupplier("");
      setNotes("");
      setErrors({});
      setTouched({});
      setSavedOk(false);
    }
  }, [isOpen]);

  if (!isOpen || !result) return null;

  const validateField = (name, value) => {
    if (name === 'batchName') {
      if (!value?.trim()) return 'El nombre del lote es obligatorio';
      if (value?.trim()?.length < 2) return 'Mínimo 2 caracteres';
    }
    if (name === 'supplier') {
      if (!value?.trim()) return 'El nombre del proveedor es obligatorio';
      if (value?.trim()?.length < 2) return 'El nombre del proveedor es obligatorio (mín. 2 caracteres)';
    }
    if (name === 'notes') {
      if (value?.length > 500) return 'Máximo 500 caracteres';
    }
    return null;
  };

  const handleBlur = (name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleChangeField = (name, value, setter) => {
    setter(value);
    if (touched?.[name]) {
      const err = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: err }));
    } else {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateAll = () => {
    const newErrors = {};
    const batchErr = validateField('batchName', batchName);
    if (batchErr) newErrors.batchName = batchErr;
    const supplierErr = validateField('supplier', supplier);
    if (supplierErr) newErrors.supplier = supplierErr;
    const notesErr = validateField('notes', notes);
    if (notesErr) newErrors.notes = notesErr;
    setErrors(newErrors);
    setTouched({ batchName: true, supplier: true, notes: true });
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateAll()) return;
    setIsSaving(true);
    try {
      if (existingBatchId) {
        const weight = parseFloat(formData?.weight_t) || 0;
        const salePrice = parseFloat(result?.maxPurchasePrice) || 0;
        const purchasePrice = parseFloat(formData?.purchase_price) || 0;
        const realizedMargin = salePrice - purchasePrice;
        const totalMargin = realizedMargin * weight;

        const { error: saleErr } = await salesService?.create({
          batch_id: existingBatchId,
          sale_price_eur_per_ton: salePrice,
          real_yield_pct: parseFloat(formData?.yield_est) || null,
          buyer_name: supplier || null,
          buyer_notes: notes || null,
          sold_at: new Date()?.toISOString(),
          realized_margin_eur_per_ton: realizedMargin,
          total_margin_eur: totalMargin,
          metal_name: formData?.metal || null,
          category_name: formData?.category || null,
          weight_t: weight,
        });
        if (saleErr) throw saleErr;

        const { error: batchErr } = await batchesService?.update(existingBatchId, {
          batch_status: 'Completado',
          actual_margin: realizedMargin,
        });
        if (batchErr) throw batchErr;

        setSavedOk(true);
        setTimeout(() => {
          setSavedOk(false);
          onSave?.({ batchName, supplier, notes, sold: true });
          onClose?.();
        }, 1200);
      } else {
        const grossKg = parseFloat(formData?.weightGross) || 0;
        const tareKg = parseFloat(formData?.weightTare) || 0;
        const netKg = Math.max(0, grossKg - tareKg);
        const yieldPct = parseFloat(formData?.yieldPct) || 90;
        const weightT = netKg / 1000;
        const weightRemaining = weightT * yieldPct / 100;
        const { error: batchErr } = await batchesService?.create({
          batch_code: batchName,
          metal_id: formData?.metalId || null,
          category_id: formData?.categoryId || null,
          metal_name: formData?.metal || "",
          category_name: formData?.category || supplier || "",
          weight_t: weightT,
          weight_remaining: weightRemaining,
          yield_est: formData?.yieldPct,
          logistics_cost: parseFloat(formData?.costLogistics) || 0,
          processing_cost: parseFloat(formData?.costProcessing) || 0,
          processing_days: formData?.daysToSale,
          target_margin: formData?.targetMargin,
          max_buy_price: result?.maxPurchasePrice,
          expected_margin: result?.targetMargin,
          purchase_price: result?.maxPurchasePrice,
          risk_buffer: result?.riskBufferDeduction || 0,
          notes: notes || null,
          batch_status: 'En Espera',
          progress: 0,
        });
        if (batchErr) throw batchErr;

        setSavedOk(true);
        addToast(`Lote ${batchName} guardado en inventario`, 'success');
        setTimeout(() => {
          setSavedOk(false);
          onSave?.({ batchName, supplier, notes });
          onClose?.();
        }, 1200);
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, _submit: err?.message || 'Error al guardar. Inténtalo de nuevo.' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)" }}>
      <div className="w-full max-w-md rounded-xl shadow-xl" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--color-border)" }}>
          <div className="flex items-center gap-2">
            <Icon name="Save" size={18} color="var(--color-accent)" strokeWidth={2} />
            <h3 className="font-heading font-semibold text-base" style={{ color: "var(--color-text-primary)" }}>
              Guardar Lote
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-standard">
            <Icon name="X" size={16} color="var(--color-text-secondary)" strokeWidth={2} />
          </button>
        </div>

        {/* Success state */}
        {savedOk && (
          <div className="mx-5 mt-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <Icon name="CheckCircle" size={16} color="#059669" />
            <span className="text-sm text-emerald-700" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Lote guardado correctamente</span>
          </div>
        )}

        {/* Summary */}
        <div className="mx-5 mt-4 rounded-lg p-3" style={{ background: "var(--color-muted)" }}>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Metal:</span>
              <span className="ml-2 font-medium capitalize" style={{ color: "var(--color-text-primary)" }}>{formData?.metal}</span>
            </div>
            <div>
              <span style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Precio Máx:</span>
              <span className="ml-2 font-data font-semibold" style={{ color: "var(--color-accent)" }}>{fmt(result?.maxPurchasePrice)}</span>
            </div>
            <div>
              <span style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Peso Neto:</span>
              <span className="ml-2 font-data" style={{ color: "var(--color-text-primary)" }}>{result?.netWeight} kg</span>
            </div>
            <div>
              <span style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Total:</span>
              <span className="ml-2 font-data font-semibold" style={{ color: "var(--color-accent)" }}>{fmt(result?.totalBatchPrice)}</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {/* Batch name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Nombre del Lote <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Ej: Lote Cobre Brillante #001"
              value={batchName}
              onChange={(e) => handleChangeField('batchName', e?.target?.value, setBatchName)}
              onBlur={() => handleBlur('batchName', batchName)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                borderColor: errors?.batchName ? 'var(--color-error)' : 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-text-primary)',
                fontFamily: "'IBM Plex Sans', sans-serif",
                outline: 'none',
              }}
            />
            <FieldError msg={errors?.batchName} />
          </div>

          {/* Supplier / Buyer */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Proveedor <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Nombre del proveedor"
              value={supplier}
              onChange={(e) => handleChangeField('supplier', e?.target?.value, setSupplier)}
              onBlur={() => handleBlur('supplier', supplier)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                borderColor: errors?.supplier ? 'var(--color-error)' : 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-text-primary)',
                fontFamily: "'IBM Plex Sans', sans-serif",
                outline: 'none',
              }}
            />
            <FieldError msg={errors?.supplier} />
          </div>

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium" style={{ color: 'var(--color-text-primary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Notas
              </label>
              <span className="text-xs" style={{ color: notes?.length > 500 ? 'var(--color-error)' : 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {notes?.length}/500
              </span>
            </div>
            <textarea
              rows={3}
              placeholder="Observaciones adicionales..."
              value={notes}
              onChange={(e) => handleChangeField('notes', e?.target?.value, setNotes)}
              onBlur={() => handleBlur('notes', notes)}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{
                borderColor: errors?.notes ? 'var(--color-error)' : 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-text-primary)',
                fontFamily: "'IBM Plex Sans', sans-serif",
                outline: 'none',
              }}
            />
            <FieldError msg={errors?.notes} />
          </div>

          {errors?._submit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <Icon name="AlertCircle" size={14} color="#ef4444" />
              <p className="text-xs text-red-600" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?._submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 pb-5">
          <Button variant="outline" fullWidth onClick={onClose} disabled={isSaving}>Cancelar</Button>
          <Button
            variant="default"
            fullWidth
            loading={isSaving}
            disabled={isSaving}
            iconName={isSaving ? undefined : "Save"}
            iconPosition="left"
            onClick={handleSave}
          >
            {isSaving ? 'Guardando...' : 'Guardar Lote'}
          </Button>
        </div>
      </div>
    </div>
  );
}